import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { orbitTokens, orbitType } from '../styles';

export type OrbitToastTone = 'saved' | 'failed';

export interface IOrbitToastBubbleProps {
  tone: OrbitToastTone;
  message: string;
  /**
   * Drawn ahead of the message. The saved toast carries a separate tick node (`5143:1491`); the
   * not-saved ones have their warning sign inline in the string, and so pass nothing.
   */
  glyph?: React.ReactNode;
  /** Renders the close control. Omitted, the bubble is inert and the caller times it out itself. */
  onDismiss?: () => void;
  dismissLabel?: string;
  testId?: string;
}

const TONE_FILL: Record<OrbitToastTone, string> = {
  saved: orbitTokens.positive,
  failed: orbitTokens.negative
};

/**
 * Width is content-driven: the frames' 384 / 433 / 460 are what their own three strings measure.
 * DELIBERATE DEVIATION — the ink is the panel surface, not the drawn `#FFFFFF`. White is 2.53:1 on
 * `positive` and 3.13:1 on `negative`; at Inter Bold 11.3 the 4.5:1 floor applies and both fail.
 * `surface` gives 7.24:1 and 5.85:1, which is the same ruling already taken for `bubbleUserInk`.
 *
 * The 11.3 the drawn strings carry is the scaled-thread artifact figma-spec-v2.md § 4b diagnoses,
 * and figma-spec-v3.md:145 names this bubble as one of its instances: "use 12.5 Bold, the v2
 * section 4b anchor". PLA-9563 § 7b.
 */
const Bubble = styled.div<{ $tone: OrbitToastTone }>`
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  min-height: 36px;
  margin: 0 auto;
  padding: 9px 12px;
  border-radius: 10px;
  background: ${({ $tone }) => TONE_FILL[$tone]};
  color: ${orbitTokens.surface};
  font-family: ${orbitTokens.fontFamily};
  ${orbitType.containerBody}
  font-weight: 700;
`;

// `5143:1491` is Inter Bold 16 and ends 5px short of the text it leads. Its own box is 19 high and
// the bubble's is 36, so the line box is the message's 18: the glyph's would make the bubble 37.
const Glyph = styled.span`
  flex: 0 0 auto;
  margin-right: 5px;
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
`;

/**
 * Wraps rather than widening the bubble past its gutters: a live subject can be far longer than the
 * frames' "Customer Engagement", and the host allows the bubble 15px less than the panel on each
 * side. `min-width: 0` is what lets a flex item shrink below its content at all; `overflow-wrap`
 * covers the single unbroken token the first two rules cannot break.
 */
const Message = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
`;

/**
 * Not drawn: no frame gives the bubble a control. It is what makes the invented auto-dismiss
 * timing answerable by the user. Its footprint is 12px because the longest drawn bubble already
 * spends 460 of the panel's 489, and the pseudo-element takes the hit area back to WCAG 2.5.8's
 * 24px without spending any of that width.
 */
const Dismiss = styled.button`
  position: relative;
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  margin-left: 3px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  ${orbitType.helping}
  cursor: pointer;
  opacity: 0.75;

  &::after {
    position: absolute;
    inset: -6px;
    content: '';
  }

  &:hover,
  &:focus-visible {
    opacity: 1;
  }
`;

/**
 * Toast variant of `div.msgBubble`: a centred bubble rather than a message in a row — no avatar, no
 * per-corner radius, no stroke. `5143:1489`, `5140:1482` and `5140:1485` all draw it 36 high at
 * radius 10, padding 9 / 12, with the string at Inter Bold 11.3 on an 18px line box.
 */
const OrbitToastBubble: React.FC<IOrbitToastBubbleProps> = ({
  tone,
  message,
  glyph,
  onDismiss,
  dismissLabel,
  testId = 'orbit-toast-bubble'
}) => {
  const perx3 = usePerx3Redesign();

  return (
    <Bubble role="status" aria-live="polite" $tone={tone} data-testid={testId} data-tone={tone}>
      {glyph !== undefined ? <Glyph aria-hidden="true">{glyph}</Glyph> : null}
      <Message data-testid={`${testId}-message`}>{message}</Message>
      {onDismiss !== undefined ? (
        <Dismiss
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          data-testid={`${testId}-dismiss`}
        >
          {/* PERX3-REDESIGN round 7: the antd close glyph under the flag, the drawn ✕ without it.
              The accessible name is the button's `aria-label` either way. */}
          <span aria-hidden="true">{perx3 ? <CloseOutlined /> : '✕'}</span>
        </Dismiss>
      ) : null}
    </Bubble>
  );
};

export default OrbitToastBubble;
